import { useContext, useMemo } from 'react';

import { type AdvancedFilterOperators } from '../../../types';
import {
  type AdvancedFiltersCondition,
  type AdvancedFiltersState,
} from '../advanced-filters';
import { AdvancedFiltersContext } from '../advanced-filters/v1/AdvancedFilters.context';
import { advancedFilterConditionIsComplete } from '../advanced-filters/v1/AdvancedFilters.utilities';
import { GeneralFiltersContext } from '../general-filters/GeneralFilters.context';

import { getTotalActiveFiltersCount } from './FiltersPopover.helpers';
import { type TotalFiltersCountPayload } from './types';

export const useActiveFiltersCount = () => {
  const generalFiltersContext = useContext(GeneralFiltersContext);
  const advancedFiltersContext = useContext(AdvancedFiltersContext);
  const { toggledOptions } = generalFiltersContext || {};
  const [state] = advancedFiltersContext || [];

  const activeFiltersCountPayload: TotalFiltersCountPayload = {};

  if (toggledOptions) {
    activeFiltersCountPayload.selectedGeneralFilters = toggledOptions;
  }
  if (state) {
    activeFiltersCountPayload.activeAdvancedFilters = state;
  }

  const activeFiltersCount = useMemo(
    () => getTotalActiveFiltersCount(activeFiltersCountPayload),
    [activeFiltersCountPayload],
  );

  return { activeFiltersCount };
};

export const useActiveFilters = () => {
  const generalFiltersContext = useContext(GeneralFiltersContext);
  const advancedFiltersContext = useContext(AdvancedFiltersContext);

  const selectedGeneralFilters = generalFiltersContext?.toggledOptions ?? {};

  const [state] = advancedFiltersContext || [];

  const activeAdvancedFilters: AdvancedFiltersState | null = useMemo(() => {
    if (!state?.conditions) return null;

    const conditions: AdvancedFiltersCondition<
      string,
      AdvancedFilterOperators
    >[] = [];

    for (const condition of Object.values(state?.conditions)) {
      if (advancedFilterConditionIsComplete(condition)) {
        conditions.push(
          condition as AdvancedFiltersCondition<
            string,
            AdvancedFilterOperators
          >,
        );
      }
    }

    return { ...state, conditions };
  }, []);

  return { selectedGeneralFilters, activeAdvancedFilters };
};
